import {type ReportMatrixBlock} from '@/api/reporting';

export type MatrixGrid = {
  readonly rows: string[];
  readonly columns: string[];
  readonly value: (row: string, column: string) => number | null;
};

const key = (row: string, column: string) => `${row}\u0000${column}`;

/**
 Grid rows in the order the query returned them, columns sorted ascending (or as returned, when the
 block says so); a missing cell is null.
 */
export function pivot(block: ReportMatrixBlock): MatrixGrid {
  const rows = new Set<string>();
  const columns = new Set<string>();
  const cells = new Map<string, number | null>();
  for (const row of block.rows) {
    const name = String(row[block.row.field] ?? '');
    const column = String(row[block.column.field] ?? '');
    const value = row[block.value.field];
    rows.add(name);
    columns.add(column);
    cells.set(key(name, column), typeof value === 'number' ? value : null);
  }

  return {
    rows: [...rows],
    columns: block.column.order === 'returned' ? [...columns] : [...columns].sort(),
    value: (row, column) => cells.get(key(row, column)) ?? null
  };
}

export const PERCENT_BANDS = ['0–25%', '25–50%', '50–75%', '75–100%'] as const;

/**
 How strongly to shade a cell, 0 to 1, or null to leave it plain. A percent is banded in fixed
 quarters, as the sheet analysts know; anything else is its share of the block's largest value.
 */
export function heat(
  value: number | null,
  format: ReportMatrixBlock['format'],
  max: number
): number | null {
  if (value === null) {
    return null;
  }

  if (format === 'percent') {
    const band = Math.min(PERCENT_BANDS.length - 1, Math.max(0, Math.floor(value * 4)));
    return (band + 1) / PERCENT_BANDS.length;
  }

  return max > 0 && value > 0 ? value / max : null;
}

/** A cell's background: the design system's accent ramp at the cell's strength. */
export function heatBackground(level: number): string {
  return `color-mix(in oklab, var(--chart-2) ${Math.round(level * 55)}%, transparent)`;
}

// `2026-04`, or the first of the month as a date, `2026-04-01`, which is what a query's DATE comes back as.
const MONTH = /^(\d{4})-(\d{2})(?:-01)?$/;
// Spelled out rather than Intl, whose en-GB short month is `Sept`; the sheet reads `Sep`.
const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/** `2026-04` or `2026-04-01` reads as `Apr 2026`; anything else is shown as returned. */
export function formatMonth(value: string): string {
  const match = MONTH.exec(value);
  const name = match ? MONTH_NAMES[Number(match[2]) - 1] : undefined;
  return match && name ? `${name} ${match[1]}` : value;
}
